import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Review } from '../interfaces/domain/reviews.modal';
import { IReviewsService } from '../interfaces/services/reviews-service.interface';
import { kReviewsPageSize } from '../services/constants';
import { ServicesHandler } from '../services/service-handler';
import GlobalContext from '../context/global-context';
import { OwApp } from '../interfaces/domain/ow-app.modal';
import { useUserProfile } from '../context/user-profile-context';

interface UseReviewsHook {
  reviews: Review[];
  slideReviews: Review[];
  isLoading: boolean;
  loadMoreReviews: (app: OwApp, page: number) => Promise<void>;
  getMyReviews: (app: OwApp) => Promise<Review[]>;
  publishReview: (
    app: OwApp,
    rating: number,
    content: string,
  ) => Promise<boolean>;
  deleteReview: (app: OwApp) => Promise<boolean>;
}

// -----------------------------------------------------------------------------
export const useReviews = (initialReviews: Review[]): UseReviewsHook => {
  const reviewsServiceRef = useRef<IReviewsService>(null);
  const [reviews, setReviews] = useState<Review[]>(initialReviews);
  const { currentUser } = useUserProfile();
  const findSlideReviews = (reviews: Review[]): Review[] => {
    if (!reviews || reviews?.length === 0) {
      return null;
    }

    const defaultAmountOfSlideReviews = 6;

    const reviewsWithText = reviews?.filter((review) => {
      return (
        review.comment_content !== '' &&
        currentUser?.profile?.nickname !== review?.comment_author
      );
    });

    const amountToSlice =
      reviewsWithText.length > defaultAmountOfSlideReviews
        ? defaultAmountOfSlideReviews
        : reviewsWithText.length;

    return reviewsWithText
      .sort((a, b) => {
        return b.rate - a.rate;
      })
      .slice(0, amountToSlice);
  };

  const [slideReviews, setSlideReviews] = useState<Review[]>(
    findSlideReviews(initialReviews),
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { config } = useContext(GlobalContext);

  useEffect(() => {
    const { reviewsService } = ServicesHandler.getInstance(config);
    reviewsServiceRef.current = reviewsService;
  }, []);

  useEffect(() => {
    setSlideReviews(findSlideReviews(reviews));
  }, [reviews, currentUser?.profile?.nickname]);

  const loadMoreReviews = useCallback(
    async (app: OwApp, page: number): Promise<void> => {
      setIsLoading(true);

      const statAppid = app?.alternativeStatsAppId
        ? app.alternativeStatsAppId
        : app.id;

      const newReviews = await reviewsServiceRef.current.getAdditionalReviews(
        statAppid,
        page,
        kReviewsPageSize,
      );

      setIsLoading(false);
      if (!newReviews) return;

      setReviews((cur) => [...cur].concat(newReviews));
    },
    [],
  );

  const publishReview = useCallback(
    async (app: OwApp, rating: number, content: string): Promise<boolean> => {
      const statAppid = app?.alternativeStatsAppId
        ? app.alternativeStatsAppId
        : app?.id;

      if (!statAppid || !reviewsServiceRef.current) {
        return false;
      }

      return reviewsServiceRef.current.publishReview(
        statAppid,
        rating,
        content,
      );
    },
    [],
  );

  const getMyReviews = useCallback(async (app: OwApp): Promise<Review[]> => {
    const statAppid = app?.alternativeStatsAppId
      ? app.alternativeStatsAppId
      : app?.id;

    if (!statAppid || !reviewsServiceRef.current) {
      return null;
    }

    return reviewsServiceRef.current.getMyReviews(statAppid);
  }, []);

  const deleteReview = useCallback(async (app: OwApp): Promise<boolean> => {
    const statAppid = app?.alternativeStatsAppId
      ? app.alternativeStatsAppId
      : app?.id;

    if (!statAppid || !reviewsServiceRef.current) {
      return false;
    }

    return reviewsServiceRef.current.deleteReview(statAppid);
  }, []);

  return {
    reviews,
    isLoading,
    slideReviews,
    loadMoreReviews,
    getMyReviews,
    publishReview,
    deleteReview,
  };
};
